import axios, { type AxiosError, type AxiosProgressEvent, type Method } from 'axios';
import ApiError from 'errors/api';
import { apiResponseFormatter } from 'formatters/apiResponse';
import authSessionService, { type IAuth } from 'services/authSession';
import userService from 'services/user';
import { v4 } from 'uuid';

import { notification } from '@eduzz/ui-antd-theme';

import { API_ENDPOINT, REACT_SECRET, REACT_TOKEN } from '../settings';

type RequestOptions = {
  retry?: boolean;

  supressError?: boolean;

  ignoreErrorHandler?: boolean;

  timeout?: number;
};

export type UploadProgressHandler = (progress: AxiosProgressEvent) => void;

const API_ERROR_CODES = {
  INVALID_JWT: '0030',

  CONFLICT_DP: 'ERR_DP409'
} as const;

const neverResolvingPromise = <T>() => new Promise<T>(() => {});

export class ApiService {
  private tokenRefreshPromise: Promise<IAuth> | null = null;

  private sessionInvalidated = false;

  constructor(private apiEndpoint: string) {}

  public get<T = any>(url: string, params?: any, options?: RequestOptions): Promise<T> {
    return this.request('GET', url, params, options);
  }

  public post<T = any>(url: string, body: any, options?: RequestOptions): Promise<T> {
    return this.request('POST', url, body, options);
  }

  public postWithUploadProgress<T = any>(
    url: string,
    body: any,
    onUploadProgress: UploadProgressHandler,
    options?: RequestOptions
  ): Promise<T> {
    return this.request('POST', url, body, options, false, null, onUploadProgress);
  }

  public put<T = any>(url: string, body: any): Promise<T> {
    return this.request('PUT', url, body);
  }

  public patch<T = any>(url: string, body: any): Promise<T> {
    return this.request('PATCH', url, body);
  }

  public delete<T = any>(url: string, params?: any): Promise<T> {
    return this.request('DELETE', url, params);
  }

  public getWithHeader<T = any>(url: string, params: any, headers: any, options?: RequestOptions): Promise<T> {
    return this.request('GET', url, params, options, false, headers);
  }

  public postWithHeader<T = any>(url: string, body: any, headers: any): Promise<T> {
    return this.request('POST', url, body, null, false, headers);
  }

  public putWithHeader<T = any>(url: string, body: any, headers: any): Promise<T> {
    return this.request('PUT', url, body, null, false, headers);
  }

  public resetSessionForAuth(): void {
    this.sessionInvalidated = false;
  }

  public handleErrorMessage(err: any) {
    if (err.message === 'Você não possui permissão para realizar esta ação') {
      notification.error({ message: err.message });
    } else {
      if (err.details !== undefined) {
        const errMsg = Array.isArray(err.details) ? err.details[0] : err.details;

        notification.error({ message: errMsg });
      } else {
        notification.error({ message: 'Ocorreu um erro. Tente novamente' });
      }
    }
  }

  private async request<T>(
    method: Method,

    url: string,

    data: any = null,

    option: RequestOptions = null,

    ignoreBearerToken = false,

    customHeaders: any = null,

    onUploadProgress?: UploadProgressHandler
  ): Promise<T> {
    if (this.sessionInvalidated) {
      return neverResolvingPromise<T>();
    }

    option = option || {};

    option = {
      retry: false,

      supressError: false,

      ...option
    };

    try {
      const token = ignoreBearerToken ? null : await this.resolveBearerToken();

      const headers = {
        'token': REACT_TOKEN,

        'secret': REACT_SECRET,

        'X-Request-Uuid-Signature': v4(),

        'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',

        ...customHeaders,

        ...(token ? { Authorization: token } : null)
      };

      const res = await axios.request({
        baseURL: this.apiEndpoint,

        url,

        method,

        timeout: option.timeout || 90000,

        headers: {
          'Content-type': 'application/json',

          ...headers
        },

        params: method === 'GET' ? data : null,

        data: method !== 'GET' ? data : null,

        onUploadProgress
      });

      onUploadProgress?.({
        loaded: 1,
        total: 1,
        progress: 1,
        bytes: 0,
        estimated: 0,
        lengthComputable: true,
        upload: true
      });

      if (res?.data.code === API_ERROR_CODES.CONFLICT_DP) {
        throw res;
      }

      return apiResponseFormatter(res.data) || undefined;
    } catch (err) {
      return this.handleError(err as AxiosError, option);
    }
  }

  private async resolveBearerToken(): Promise<string | null> {
    const { tokens, user } = authSessionService.getSession();

    if (!tokens || !user) {
      return null;
    }

    const now = (Date.now().valueOf() + 500) / 1000;

    if (!user.exp || user.exp > now) {
      return `Bearer ${tokens.token}`;
    }

    try {
      const refreshed = await this.refreshTokens(tokens.refresh);

      return refreshed?.token ? `Bearer ${refreshed.token}` : null;
    } catch {
      this.clearRefreshToken();

      window.location.reload();

      return null;
    }
  }

  private refreshTokens(refresh: string): Promise<IAuth> {
    if (!this.tokenRefreshPromise) {
      this.tokenRefreshPromise = this.request<{ data: { jwt_token: string; jwt_refresh_token: string } }>(
        'POST',

        '/auth_v2/refresh',

        { jwt_refresh_token: refresh },

        {},

        true
      )

        .then(response => {
          const tokens: IAuth = {
            token: response.data.jwt_token,

            refresh: response.data.jwt_refresh_token
          };

          authSessionService.setTokens(tokens);

          return tokens;
        })

        .finally(() => {
          this.tokenRefreshPromise = null;
        });
    }

    return this.tokenRefreshPromise;
  }

  private handleError(err: AxiosError, option: RequestOptions): Promise<never> {
    if (option.ignoreErrorHandler) {
      throw err;
    }

    if (!err.config) {
      return this.formatError(err, option);
    }

    if (err.response?.status === 401) {
      return this.handleSessionInvalidated();
    }

    if ((err.response?.data as any)?.dataValidation) {
      notification.error({ message: 'Por favor preencha todos os campos corretamente' });
    }

    return this.formatError(err, option);
  }

  private handleSessionInvalidated(): Promise<never> {
    if (this.sessionInvalidated) {
      return neverResolvingPromise();
    }

    this.sessionInvalidated = true;

    this.clearRefreshToken();

    userService.logout();

    return neverResolvingPromise();
  }

  private clearRefreshToken(): void {
    authSessionService.clearSession();
  }

  private formatError(err: AxiosError, option: RequestOptions): Promise<never> {
    if (option.supressError) {
      return neverResolvingPromise();
    }

    if (!err.config) {
      throw err;
    }

    throw new ApiError(err.config, err.response, err);
  }
}

const apiService = new ApiService(API_ENDPOINT);

export default apiService;
